/**
 * extractDisplay — plain-language presentation layer for the capture modal's
 * "Extracted fields" review pane (NRA smoke test, Bill 2026-06-12).
 *
 * The wire/extract store keys (fullName / dob / pcbIdNumber / fornTaxId / …)
 * are INTERNAL mapping names and must never face the FSR. This module:
 *
 *   1. LABELS — maps every backend key the capture pipeline emits to plain
 *      banking language ("Full name", "Date of birth", "Passport number").
 *   2. DEDUPE — the raw analyzer rows + the pcbId* CIP family (Core Director
 *      F254–F258) + the id* family are different internal mappings of the
 *      SAME facts. Each logical fact renders ONCE (with its real confidence);
 *      the underlying mappings in the patch/extract store stay intact.
 *   3. ORDER — identity (names, DOB, gender) → document identifiers (type,
 *      number, issuer) → dates (issue, expiry) → document-specific extras →
 *      MRZ last, collapsed/secondary.
 *   4. CORRECTIONS — inline edits made at the Apply step fan out to every
 *      underlying member key of the fact, and `correctionsToPersonPatch`
 *      converts them into the RaoPerson patch the host applies (officer-typed
 *      values win over the raw extract — D1: low-confidence surfaces at Apply).
 *
 * Field sources: NraScanFields wire contract (packages/api-client/src/domains/
 * v3-nra.ts), ExtractedIdFields (IdExtractionService), and the per-doc extract
 * store person.extra.extracts.* (BACKLOG-0601). CIP family = Core Director
 * F254–F258 (live ParmValSrch probe 2026-05-27, PROD Core Director). No new
 * data fields are introduced here — display-only relabeling of existing
 * pipeline outputs.
 */

import type { RaoPerson } from '../../../types';
import { normalizeIso2 } from '../../../utils/isoCountry';
import { joinStreetLines } from '../shared/streetLine';

// -----------------------------------------------------------------------------
// Conflict surfacing (North-Star brief §2.6 — major conflict → ALERT, FSR picks)
// -----------------------------------------------------------------------------

export type ConflictSeverity = 'major' | 'minor';

/**
 * A stated-vs-extracted conflict the capture pipeline detected (e.g. the FSR
 * pre-selected one citizenship country but the passport reads another).
 * Surfaced as an alert in the capture modal review pane; the FSR must pick a
 * side before Apply. NEVER silently overwrites a stated value (§2.6 tiering).
 */
export interface CaptureConflict {
  /** Stable person-field id — also the correction key when the FSR picks the document side. */
  field: string;
  /** Plain-language label ("Country of citizenship"). */
  label: string;
  /** The value already on the person (display form). */
  stated: string;
  /** The value the document reads (display form). */
  extracted: string;
  /** Normalized value persisted when the FSR picks the document side. */
  extractedPatchValue: string;
  severity: ConflictSeverity;
}

// -----------------------------------------------------------------------------
// F254 IdVerifyCode decode (live ParmValSrch probe 2026-05-27)
// -----------------------------------------------------------------------------

const ID_TYPE_F254_LABELS: Readonly<Record<string, string>> = {
  '0': 'Blank',
  '1': "Driver's License",
  '2': 'State ID',
  '3': 'Passport',
  '4': 'Military ID',
  '5': 'Social Security Card',
  '6': 'State Documents',
  '7': 'Birth Certificate',
  '8': 'Other',
  '9': 'Lexis Nexis',
};

/** "3" → "3 — Passport"; unknown codes pass through unchanged. */
export function formatIdTypeF254(raw: string): string {
  const code = raw.trim();
  const label = ID_TYPE_F254_LABELS[code];
  return label ? `${code} — ${label}` : raw;
}

// -----------------------------------------------------------------------------
// Logical-fact catalog (dedupe + plain labels + order)
// -----------------------------------------------------------------------------

interface FactSpec {
  /** Stable row id (also the edit key). */
  id: string;
  /** Plain-language label. */
  label: string;
  /**
   * Underlying field keys that all express this fact, in display-priority
   * order. The first member with a REAL confidence score supplies the shown
   * value; remaining members stay in the patch untouched.
   */
  keys: ReadonlyArray<string>;
  /** False for derived/coded/raw-zone rows the FSR shouldn't free-type. */
  editable?: boolean;
  /** True → collapsed secondary rendering (MRZ raw strings). */
  secondary?: boolean;
  /** Optional display formatter for the winning value. */
  format?: (value: string) => string;
}

/**
 * THE display order. Identity → document identifiers → dates → doc-specific
 * extras → work/school → address → MRZ (secondary, last). Keys not claimed by
 * any fact fall through to a humanized tail (nothing silently disappears).
 */
const FACTS: ReadonlyArray<FactSpec> = [
  // ── Identity ──────────────────────────────────────────────────────────────
  { id: 'fullName',  label: 'Full name',  keys: ['fullName'], editable: false },
  { id: 'firstName', label: 'First name', keys: ['firstName'] },
  { id: 'middleName', label: 'Middle name', keys: ['middleName'] },
  { id: 'lastName',  label: 'Last name',  keys: ['lastName'] },
  { id: 'dob',       label: 'Date of birth', keys: ['dob', 'dateOfBirth'] },
  { id: 'gender',    label: 'Gender',     keys: ['gender', 'sex'] },
  { id: 'countryOfCitizenship', label: 'Country of citizenship', keys: ['countryOfCitizenship'] },

  // ── Document identifiers (Core Director F254–F256 family) ────────────────
  {
    id: 'idType',
    label: 'ID type (Core Director Field 254)',
    keys: ['pcbIdType', 'idType', 'idTypeCode'],
    editable: false,
    format: formatIdTypeF254,
  },
  {
    id: 'documentNumber',
    label: 'Document number',
    keys: ['foreignPassportNumber', 'pcbIdNumber', 'idNumber', 'documentNumber'],
  },
  {
    id: 'issuer',
    label: 'Issuing country / authority',
    keys: ['foreignPassportCountry', 'pcbIdIssuer', 'idIssuer', 'issuingAuthority'],
  },
  { id: 'fornTaxId', label: 'Foreign tax ID', keys: ['fornTaxId'] },
  { id: 'taxIdNumber', label: 'Tax ID number', keys: ['ssnNumber', 'taxId'] },

  // ── Dates (Core Director F257–F258 family) ────────────────────────────────
  {
    id: 'issueDate',
    label: 'Issue date',
    keys: ['foreignPassportIssueDt', 'pcbIdIssueDate', 'idIssueDate', 'passportIssueDt', 'issueDate'],
  },
  {
    id: 'expiryDate',
    label: 'Expiration date',
    keys: ['foreignPassportExpDt', 'pcbIdExpiryDate', 'idExpiryDate', 'passportExpDt', 'expirationDate'],
  },

  // ── Visa (US-Visa foil — pcb-visa / Document-Fields path) ─────────────────
  { id: 'visaType', label: 'Visa class', keys: ['visaType'] },
  // Bill smoke #2 (2026-06-12) — the visa's OWN document number is the
  // red-printed foil number (WNumber leaf; W-prefixed on Bill's sample).
  // Distinct fact from the CEAC control number below and from the
  // "Passport number printed on visa" cross-ref.
  { id: 'visaDocumentNumber', label: 'Visa document number', keys: ['visaDocumentNumber'] },
  { id: 'visaControlNumber', label: 'Visa control number', keys: ['visaControlNumber'] },
  { id: 'visaIssuePost', label: 'Visa issuing post', keys: ['visaIssuePost'] },
  { id: 'visaIssueDt', label: 'Visa issue date', keys: ['visaIssueDt'] },
  { id: 'visaExpDt', label: 'Visa expiration', keys: ['visaExpDt'] },
  { id: 'visaEntries', label: 'Entries allowed', keys: ['visaEntries'] },
  { id: 'visaAnnotation', label: 'Visa annotation', keys: ['visaAnnotation'] },
  {
    id: 'visaPassportCrossRef',
    label: 'Passport number printed on visa',
    keys: ['visaPassportNumberCrossRef'],
  },

  // ── I-94 (CBP admission record) ───────────────────────────────────────────
  { id: 'i94Number', label: 'I-94 admission number', keys: ['i94Number'] },
  { id: 'i94AdmitClass', label: 'Class of admission', keys: ['i94AdmitClass'] },
  { id: 'i94AdmitUntil', label: 'Admitted-until date', keys: ['i94AdmitUntil'] },
  { id: 'i94ExpDt', label: 'I-94 expiration', keys: ['i94ExpDt', 'i94ExpirationDate'] },
  { id: 'i94EntryDate', label: 'Most recent entry date', keys: ['i94EntryDate'] },
  { id: 'i94EntryPort', label: 'Port of entry', keys: ['i94EntryPort'] },
  {
    id: 'i94DocCrossRef',
    label: 'Travel document number on I-94',
    keys: ['i94PassportNumberCrossRef'],
  },

  // ── I-20 (F-1 student) ────────────────────────────────────────────────────
  { id: 'sevisId', label: 'SEVIS ID', keys: ['sevisId'] },
  { id: 'schoolName', label: 'School name', keys: ['schoolName'] },
  { id: 'schoolCode', label: 'School code', keys: ['schoolCode'] },
  { id: 'programStartDt', label: 'Program start', keys: ['programStartDt'] },
  { id: 'programEndDt', label: 'Program end', keys: ['programEndDt'] },
  { id: 'educationLevel', label: 'Education level', keys: ['educationLevel'] },
  { id: 'major1', label: 'Major / field of study', keys: ['major1'] },
  { id: 'major1CipCode', label: 'Major code (CIP)', keys: ['major1CipCode'] },
  { id: 'formIssueReason', label: 'Form issue reason', keys: ['formIssueReason'] },

  // ── DS-2019 (J-1 exchange visitor) ────────────────────────────────────────
  { id: 'programSponsorName', label: 'Program sponsor', keys: ['programSponsorName'] },
  { id: 'programNumber', label: 'Program number', keys: ['programNumber'] },
  { id: 'exchangeVisitorCategory', label: 'Exchange visitor category', keys: ['exchangeVisitorCategory'] },
  { id: 'fieldCode', label: 'Field-of-study code', keys: ['fieldCode'] },
  { id: 'twoYearResidenceFlag', label: 'Two-year home-residence rule', keys: ['twoYearResidenceFlag'] },

  // ── Permanent Resident Card / EAD ─────────────────────────────────────────
  { id: 'greenCardANumber', label: 'USCIS A-Number', keys: ['greenCardANumber'] },
  { id: 'greenCardCategory', label: 'Card category', keys: ['greenCardCategory'] },
  { id: 'greenCardExpDt', label: 'Card expiration', keys: ['greenCardExpDt'] },
  { id: 'greenCardResidentSince', label: 'Resident since', keys: ['greenCardResidentSince'] },
  { id: 'eadANumber', label: 'USCIS number (EAD)', keys: ['eadANumber'] },
  { id: 'eadCategory', label: 'EAD category', keys: ['eadCategory'] },
  { id: 'eadExpDt', label: 'EAD expiration', keys: ['eadExpDt'] },

  // ── Work / School auto-fill ───────────────────────────────────────────────
  { id: 'employmentStatus', label: 'Employment status', keys: ['employmentStatus'] },
  { id: 'employer', label: 'Employer / school', keys: ['employer'] },
  { id: 'occupation', label: 'Occupation', keys: ['occupation'] },
  { id: 'naicsCode', label: 'NAICS code', keys: ['naicsCode'] },

  // ── Address (address-proof / photo-ID address block) ──────────────────────
  { id: 'addr', label: 'Street address', keys: ['addr', 'addressLine1'] },
  { id: 'addr2', label: 'Street line 2', keys: ['addr2', 'addressLine2'] },
  { id: 'city', label: 'City', keys: ['city', 'addressCity'] },
  { id: 'state', label: 'State', keys: ['state', 'addressState'] },
  { id: 'zip', label: 'ZIP code', keys: ['zip', 'addressZip'] },

  // ── MRZ — raw machine zone, collapsed + read-only, ALWAYS LAST ────────────
  {
    id: 'mrz',
    label: 'Machine-readable zone (MRZ)',
    keys: [
      'machineReadableZone', 'passportMrz', 'visaMrz',
      'passportMrzTop', 'visaMrzTop', 'passportMrzBottom', 'visaMrzBottom',
    ],
    editable: false,
    secondary: true,
  },
];

/** Wire/CIP key → canonical person-store key, used when fanning corrections out. */
const WIRE_TO_PERSON_KEY: Readonly<Record<string, string>> = {
  pcbIdType: 'idType',
  pcbIdNumber: 'idNumber',
  pcbIdIssuer: 'idIssuer',
  pcbIdIssueDate: 'idIssueDate',
  pcbIdExpiryDate: 'idExpiryDate',
  idTypeCode: 'idType',
  dateOfBirth: 'dob',
  sex: 'gender',
  documentNumber: 'idNumber',
  issuingAuthority: 'idIssuer',
  issueDate: 'idIssueDate',
  expirationDate: 'idExpiryDate',
  addressLine1: 'addr',
  addressLine2: 'addr2',
  addressCity: 'city',
  addressState: 'state',
  addressZip: 'zip',
  i94ExpirationDate: 'i94ExpDt',
};

/** RaoPerson first-class keys (everything else corrects into person.extra). */
const TOP_LEVEL_PERSON_KEYS: ReadonlySet<string> = new Set([
  'firstName', 'middleName', 'lastName', 'dob', 'gender',
  'addr', 'addr2', 'city', 'state', 'zip',
  'countryOfCitizenship', 'foreignPassportNumber', 'foreignPassportCountry',
  'visaType', 'visaExpDt', 'i94Number', 'i94ExpDt',
  'employmentStatus', 'employer', 'occupation', 'naicsCode',
  'taxId', 'fornTaxId',
]);

// -----------------------------------------------------------------------------
// Row building
// -----------------------------------------------------------------------------

export interface ExtractFieldValue {
  value: string | null;
  confidence: number;
}

export interface ExtractDisplayRow {
  /** Stable row id — also the inline-edit key. */
  factId: string;
  /** Plain-language label. */
  label: string;
  /** Raw underlying value (pre-format) — the edit seed. */
  value: string;
  /** Display value (post-format, e.g. F254 decode). */
  displayValue: string;
  /** Best REAL confidence among the members; negative = manual/derived. */
  confidence: number;
  editable: boolean;
  secondary: boolean;
  /** Underlying member keys present in the field map (correction fan-out). */
  memberKeys: ReadonlyArray<string>;
  /** The document did not yield this value; the row is shown empty so the officer can type it. */
  missing?: boolean;
}

/** "camelCaseKey" → "Camel case key" — last-resort label for unmapped keys. */
function humanizeKey(key: string): string {
  const spaced = key
    .replace(/([a-z\d])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
}

/**
 * Reduce a raw `{ key: { value, confidence } }` field map into ordered,
 * deduplicated, plain-language display rows. Each logical fact appears ONCE:
 * the first member key with a REAL (≥0) confidence wins; when no member has a
 * real score the first present member wins and the row shows "manual".
 * Unmapped keys append after the catalog (humanized), before MRZ.
 */
export function buildDisplayRows(
  fields: Record<string, ExtractFieldValue | undefined>,
): ExtractDisplayRow[] {
  const present = new Map<string, { value: string; confidence: number }>();
  for (const [k, v] of Object.entries(fields)) {
    if (v && v.value != null && v.value !== '') {
      present.set(k, { value: String(v.value), confidence: Number(v.confidence) });
    }
  }

  const claimed = new Set<string>();
  const rows: ExtractDisplayRow[] = [];
  const secondaryRows: ExtractDisplayRow[] = [];

  // Address parts travel together (Bill 2026-10-06): when the document yielded ANY part of the
  // address, every part is shown, empty ones included, so a mis-split street line ("1 Tenth
  // Street Apt 1 Augusta, ME 04330") can be corrected in the review instead of failing the
  // address check later. An empty row writes to every key of its fact on Apply.
  const ADDRESS_FACTS = new Set(['addr', 'addr2', 'city', 'state', 'zip']);
  const anyAddress = FACTS.some((f) => ADDRESS_FACTS.has(f.id) && f.keys.some((k) => present.has(k)));

  for (const fact of FACTS) {
    const members = fact.keys.filter((k) => present.has(k));
    if (members.length === 0) {
      if (anyAddress && ADDRESS_FACTS.has(fact.id)) {
        rows.push({
          factId: fact.id, label: fact.label, value: '', displayValue: '', confidence: -1,
          editable: true, secondary: false, memberKeys: fact.keys, missing: true,
        });
      }
      continue;
    }
    for (const m of members) claimed.add(m);
    // Winner = first member with a real OCR score; else first present member.
    const scoredKey = members.find((k) => (present.get(k)!.confidence) >= 0);
    const winnerKey = scoredKey ?? members[0]!;
    const winner = present.get(winnerKey)!;
    const row: ExtractDisplayRow = {
      factId: fact.id,
      label: fact.label,
      value: winner.value,
      displayValue: fact.format ? fact.format(winner.value) : winner.value,
      confidence: winner.confidence,
      editable: fact.editable !== false,
      secondary: fact.secondary === true,
      memberKeys: members,
    };
    (row.secondary ? secondaryRows : rows).push(row);
  }

  // Tail — anything the catalog doesn't know about still renders (humanized)
  // so a new wire field can never silently disappear from the review pane.
  for (const [k, v] of present.entries()) {
    if (claimed.has(k)) continue;
    rows.push({
      factId: k,
      label: humanizeKey(k),
      value: v.value,
      displayValue: v.value,
      confidence: v.confidence,
      editable: true,
      secondary: false,
      memberKeys: [k],
    });
  }

  return [...rows, ...secondaryRows];
}

// -----------------------------------------------------------------------------
// Corrections (Apply-step officer edits — win over the raw extract)
// -----------------------------------------------------------------------------

/**
 * Fan inline edits (keyed by factId) out to every underlying member key of
 * the fact, normalized to the canonical person-store key. The returned map is
 * the `ScanResultPayload.corrections` contract the capture host converts into
 * a person patch via `correctionsToPersonPatch`.
 */
export function correctionsForEdits(
  rows: ReadonlyArray<ExtractDisplayRow>,
  edits: Readonly<Record<string, string>>,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const row of rows) {
    const edited = edits[row.factId];
    if (edited === undefined) continue;
    const next = edited.trim();
    if (next === row.value) continue; // no-op edit
    for (const member of row.memberKeys) {
      out[WIRE_TO_PERSON_KEY[member] ?? member] = next;
    }
  }
  return out;
}

/** "M"/"male"/"Male" → "Male"; "F…" → "Female"; anything else passes through. */
function normalizeGender(raw: string): string {
  const up = raw.trim().toUpperCase();
  if (up.startsWith('M')) return 'Male';
  if (up.startsWith('F')) return 'Female';
  return raw.trim();
}

/**
 * Convert a corrections map into the RaoPerson patch the host applies.
 * First-class RaoPerson keys land top-level; everything else lands under
 * `extra` (the hosts' extra-aware appliers deep-merge one level, same as the
 * CIP slot-machine effect's only-new-keys extra patches). Officer corrections
 * are EXPLICIT input — applied unconditionally, never empty-fields-only.
 */
export function correctionsToPersonPatch(
  corrections: Readonly<Record<string, string>>,
  current?: { addr?: string | null },
): Partial<RaoPerson> {
  const patch: Partial<RaoPerson> = {};
  const extra: Record<string, string> = {};
  for (const [key, rawValue] of Object.entries(corrections)) {
    let value = rawValue;
    if (key === 'gender') value = normalizeGender(rawValue);
    if (key === 'countryOfCitizenship' || key === 'foreignPassportCountry') {
      value = normalizeIso2(rawValue);
    }
    if (TOP_LEVEL_PERSON_KEYS.has(key)) {
      (patch as Record<string, unknown>)[key] = value;
    } else {
      extra[key] = value;
    }
  }
  if (Object.keys(extra).length > 0) patch.extra = extra;
  // Street line 2 (apartment / unit) is not a separate slot on the person: it joins the
  // street, so it shows on the form, goes to USPS and reaches Core (Bill 2026-10-06).
  if (patch.addr2 !== undefined) {
    const street = joinStreetLines(patch.addr ?? current?.addr ?? '', patch.addr2);
    if (street) patch.addr = street;
    patch.addr2 = '';
  }
  return patch;
}
